import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { reduxForm, formValueSelector, change } from 'redux-form';
import { compose } from 'redux';
import { connect } from 'react-redux';
import {
  BannerComponent,
  Card,
  IconList,
  LevelNav,
  Radio,
} from 'common/components';
import { getShape } from 'common/components/MapComponent/util';
import { capitalizeString, getLatestDiary } from 'common/utils';
import { toAcres } from 'util/conversions';
import { UPDATE_SELECTED_FIELD, ACTIVITY_TYPES } from 'common/constants';
import { updateMaxPlantingArea } from 'actions/settings';

const FieldForm = props => {
  const {
    onSubmit,
    fields,
    crops,
    selectedCrop,
    activityType,
    changeValue,
    updateMaxPlantingArea,
    updatedSelectedField,
  } = props;
  // crop value is "name|variety"
  const selectedCropObj = selectedCrop && JSON.parse(selectedCrop);

  let selectedFields = [];

  // when activity type is seeding, return fields that have space
  if (activityType === ACTIVITY_TYPES.SEEDING) {
    selectedFields = fields
      .map(field => {
        const filterCrops =
          crops && crops.filter(crop => crop.fieldName === field.name);
        return {
          ...field,
          crop: filterCrops,
          availablePlantingArea:
            toAcres(field.size.size, field.size.type) -
            filterCrops.reduce(
              (sum, crop) =>
                sum + toAcres(crop.plantedArea.size, crop.plantedArea.type),
              0
            ),
        };
      })
      .filter(field => field.availablePlantingArea > 0);
  } else {
    if (selectedCropObj && selectedCropObj.name) {
      // when there's a selected crop
      selectedFields = crops
        .filter(
          crop =>
            crop.crop.name === selectedCropObj.name &&
            crop.crop.variety === selectedCropObj.variety
        )
        .map(crop => {
          const { geojson, shape } = fields.find(
            f => f.name === crop.fieldName
          );
          return {
            ...crop,
            geojson,
            shape,
          };
        });

      if (activityType === ACTIVITY_TYPES.HARVESTING) {
        // remove the field which is already harvested to prevent the same field being harvested multiple times
        selectedFields = selectedFields.filter(crop => {
          const latestYield = getLatestDiary(crop.expectedYield) || {};
          return latestYield.yield
            ? crop.crop && !latestYield.yield.harvested
            : crop.crop;
        });
      }

      // sort by plantedArea size can be done only with crops
      if (selectedFields.length > 1) {
        selectedFields.sort((a, b) => b.plantedArea.size - a.plantedArea.size);
      }
    } else {
      // when there's no selected crop (used for OTHER type)
      selectedFields = (fields || [])
        .map(field => ({
          ...field,
          crop: crops && crops.filter(crop => crop.fieldName === field.name),
        }))
        // filter out field with crops if `Empty Field`(name: null) is selected
        .filter(field =>
          selectedCropObj ? !field.crop || !field.crop.length : true
        );
    }
  }

  return (
    <Fragment>
      <BannerComponent
        theme={['static', 'strong', 'large', 'mobile-fixed']}
        message={
          <LevelNav
            nav={{
              left: {
                label: <span className={'visible--desktop'}>Back</span>,
                action: () => onSubmit(),
              },
              center: { label: 'Select Field' },
            }}
          />
        }
      />

      <form>
        <p className={'text--fade'}>
          <small>Click on the list or map to select a field.</small>
        </p>

        <div className={'field'}>
          {selectedFields &&
            selectedFields.map((field, idx) => (
              <Radio
                key={idx}
                onChange={() => {
                  updatedSelectedField(getShape(field));
                  if (field.availablePlantingArea) {
                    updateMaxPlantingArea(field.availablePlantingArea);
                    changeValue('plantedArea', field.availablePlantingArea);
                  }
                  onSubmit();
                }}
                label={
                  <Card>
                    <h3 className={'cardlist__title'}>
                      {field.fieldName || field.name}
                    </h3>
                    <IconList
                      list={
                        field.fieldName
                          ? [
                              {
                                label: `${field.crop.name} ${
                                  field.crop.variety ? field.crop.variety : ''
                                }`,
                                icon: 'crop',
                              },
                              {
                                label: `${
                                  field.plantedArea.size
                                } ${capitalizeString(field.plantedArea.type)}`,
                                icon: 'fields',
                              },
                            ]
                          : (field.crop.length
                              ? field.crop.map(crop => ({
                                  label: `${crop.crop.name} ${
                                    crop.crop.variety ? crop.crop.variety : ''
                                  }`,
                                  icon: 'crop',
                                }))
                              : []
                            ).concat([
                              {
                                label: `${field.size.size} ${capitalizeString(
                                  field.size.type
                                )}`,
                                icon: 'fields',
                              },
                            ])
                      }
                    />
                  </Card>
                }
                name={'fieldName'}
                val={JSON.stringify({
                  name: field.fieldName || field.name,
                  size: field.size ? field.size.size : field.plantedArea.size,
                })}
                theme={['card']}
              />
            ))}
        </div>
      </form>
    </Fragment>
  );
};

FieldForm.propTypes = {
  // from redux form
  error: PropTypes.string,
  handleSubmit: PropTypes.func.isRequired,
  valid: PropTypes.bool.isRequired,
  pristine: PropTypes.bool.isRequired,
  submitting: PropTypes.bool.isRequired,
  filter: PropTypes.bool.isRequired,

  // from mapstate
  selectedCrop: PropTypes.string,

  // from mapdispatch
  changeValue: PropTypes.func,
  updateMaxPlantingArea: PropTypes.func,
  updatedSelectedField: PropTypes.func,

  // manually added
  onSubmit: PropTypes.func.isRequired,
  crops: PropTypes.arrayOf(PropTypes.object),
  fields: PropTypes.arrayOf(PropTypes.object),
  activityType: PropTypes.string,
};

FieldForm.defaultProps = {
  filter: true,
};

const formSettings = {
  form: 'activityform',
  destroyOnUnmount: false,
};
const field = formValueSelector(formSettings.form);
const mapState = state => ({
  selectedCrop: field(state, 'crop'),
});

const mapDispatch = dispatch => ({
  changeValue: (fieldname, value) =>
    dispatch(change(formSettings.form, fieldname, value)),
  updateMaxPlantingArea: payload => dispatch(updateMaxPlantingArea(payload)),
  updatedSelectedField: payload =>
    dispatch({
      type: UPDATE_SELECTED_FIELD,
      payload,
    }),
});

export default compose(
  reduxForm(formSettings),
  connect(
    mapState,
    mapDispatch
  )
)(FieldForm);



// WEBPACK FOOTER //
// ./src/containers/ActivityForm/forms/Field.js